import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { logo } from "../../assets/assetsExport.tsx";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";

export default function Header(): JSX.Element {
  const [menuIsOpen, setMenuIsOpen] = useState(false);
  const [addBackground, setAddBackground] = useState(false);
  const [heroHoveredPanel, setHeroHoveredPanel] = useState<
    "left" | "center" | "right" | null
  >(null);
  const isSmallScreen = useSelector(
    (state: RootState) => state.ui.isSmallScreen
  );
  const location = useLocation();
  const [expandListStates, setExpandListStates] = useState<{
    [key: string]: boolean;
  }>({});

  const toggleExpandList = (listName: string) => {
    setExpandListStates((prevStates) => ({
      ...prevStates,
      [listName]: !prevStates[listName],
    }));
  };

  const closeAllExpandLists = () => {
    setExpandListStates({});
  };

  useEffect(() => {
    const toggleBackground = () => {
      if (document.querySelector("body")!.getBoundingClientRect().top < -20) {
        setAddBackground(true);
      } else {
        setAddBackground(false);
      }
    };

    window.addEventListener("scroll", toggleBackground);

    return () => {
      window.removeEventListener("scroll", toggleBackground);
    };
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{
        hoveredPanel: "left" | "center" | "right" | null;
      }>;
      setHeroHoveredPanel(ce.detail?.hoveredPanel ?? null);
    };
    window.addEventListener("hero:hoveredPanel", handler);
    return () => window.removeEventListener("hero:hoveredPanel", handler);
  }, []);

  const setScrollLock = (locked: boolean) => {
    const html = document.documentElement;
    const body = document.body;
    html.style.overflow = locked ? "hidden" : "";
    body.style.overflow = locked ? "hidden" : "";
  };

  function toggleMenu() {
    const next = !menuIsOpen;
    setMenuIsOpen(next);
    setScrollLock(next);
    if (!next) {
      closeAllExpandLists();
    }
  }

  function closeMenu(e?: React.MouseEvent<HTMLAnchorElement>) {
    if (!e || (e.target as HTMLElement).tagName === "A") {
      setScrollLock(false);
      setMenuIsOpen(false);
      closeAllExpandLists();
    }
  }

  function isSelected(path: string): boolean {
    return location.pathname === path;
  }

  const isLight =
    location.pathname === "/services/rent" ||
    location.pathname.startsWith("/services/rent/") ||
    location.pathname === "/services/buy" ||
    location.pathname === "/services/landlords";

  const isRentPropertiesPage =
    location.pathname === "/services/rent/properties/list" ||
    location.pathname === "/services/rent/properties/map";

  function MobileItem({ to, text }: { to: string; text: string }) {
    return (
      <li className="border-b border-b-solid border-b-[rgba(0,0,0,0.05)]">
        <NavLink
          to={to}
          onClick={() => closeMenu()}
          className="block font-teko text-2xl leading-tight text-text-obsidian py-4 uppercase transition-all duration-300 hover:pl-2.5 hover:text-brand-yellow"
        >
          {text}
        </NavLink>
      </li>
    );
  }

  // Mobile menu structure
  if (isSmallScreen) {
    return (
      <>
        {/* Mobile Nav Bar */}
        <nav className="fixed top-0 left-0 w-full h-[70px] flex justify-between items-center px-6 z-[2000] bg-white border-b-solid border-b-[0.5px] border-b-[rgba(0,0,0,0.2)]">
          <NavLink to="/">
            <img src={logo.dark} alt="Letting Engine logo" className="h-12" />
          </NavLink>
          <button
            type="button"
            onClick={toggleMenu}
            className={`font-teko text-xl uppercase tracking-wide px-4 py-1 border border-solid transform skew-x-[var(--skew)] transition-all duration-300 ${
              menuIsOpen
                ? "bg-text-obsidian text-white border-text-obsidian"
                : "border-text-obsidian text-text-obsidian hover:bg-text-obsidian hover:text-white"
            }`}
          >
            <span className="block transform skew-x-[10deg]">
              {menuIsOpen ? "Close" : "Menu +"}
            </span>
          </button>
        </nav>

        {/* Full Screen Overlay Menu */}
        <div
          className={`fixed top-0 left-0 w-full h-screen bg-bg-porcelain z-[1500] flex flex-col pt-[90px] transition-transform duration-500 ease-tech overflow-hidden ${
            menuIsOpen ? "translate-y-0" : "-translate-y-full"
          }`}
        >
          {/* Menu List */}
          <ul className="list-none p-0 px-8 overflow-y-auto flex-1">
            <MobileItem to="/" text="Home" />
            <MobileItem to="/services/rent" text="Rent" />
            <MobileItem to="/services/buy" text="Buy" />
            <MobileItem to="/services/landlords" text="Landlords" />
            {/* Tools Accordion */}
            <li className="border-b border-b-solid border-b-[rgba(0,0,0,0.05)] text-2xl">
              <button
                type="button"
                onClick={() => toggleExpandList("tools")}
                className="w-full flex justify-between items-center font-teko leading-tight text-text-obsidian py-4 uppercase transition-all duration-300 hover:pl-2.5 hover:text-brand-yellow"
              >
                Tools
                <span
                  className={`text-2xl transition-transform duration-300 ${
                    expandListStates["tools"]
                      ? "rotate-90 text-brand-yellow"
                      : ""
                  }`}
                >
                  →
                </span>
              </button>
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  expandListStates["tools"] ? "max-h-[300px] py-4" : "max-h-0"
                } bg-[rgba(0,0,0,0.03)] px-4`}
              >
                {[
                  {
                    to: "/services/stamp-duty-calculator?language=english",
                    text: "Stamp Duty Calculator",
                    border: true,
                  },
                  {
                    to: "/services/rental-yield?language=english",
                    text: "Rental Yield",
                    border: true,
                  },
                  {
                    to: "/services/mortgage-calculator?language=english",
                    text: "Mortgage Calculator",
                    border: true,
                  },
                  {
                    to: "/services/rent-estimate?language=english",
                    text: "Rent Estimate Tool",
                    border: false,
                  },
                ].map(({ to, text, border }, idx) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={() => closeMenu()}
                    className={`flex items-center gap-2.5 font-manrope text-base font-semibold text-text-slate py-3 hover:text-brand-yellow${
                      border
                        ? " border-b border-b-solid border-b-[rgba(0,0,0,0.05)]"
                        : ""
                    }`}
                  >
                    {text}
                  </NavLink>
                ))}
              </div>
            </li>
            <MobileItem to="/about-us#regulatory-stack" text="Qualifications" />
            <MobileItem to="/about-us" text="About Us" />
            <MobileItem to="/careers" text="Careers" />
          </ul>

          {/* Mobile Footer */}
          <div className="p-6 bg-text-obsidian text-white">
            <NavLink
              to="/contact-us"
              onClick={() => closeMenu()}
              className="block text-center bg-brand-yellow text-black py-1 font-teko text-2xl uppercase transform skew-x-[var(--skew)] mb-4 transition-all duration-300 hover:bg-brand-gold"
            >
              <span className="block transform skew-x-[10deg]">Contact Us</span>
            </NavLink>
            <div className="text-xs text-[#888] text-center font-manrope">
              London HQ • +44 (0)20 3907 4025
              <br />© {new Date().getFullYear()} Letting Engine
            </div>
          </div>
        </div>
      </>
    );
  }

  const hasBackground = addBackground || isRentPropertiesPage;

  const headerClass = [
    "fixed top-0 left-0 w-full z-[1000000000]",
    "transition-[max-height,height,background-color] duration-200",
    "flex items-center justify-between",
    hasBackground
      ? "bg-white border-b border-[rgba(0,0,0,0.2)] max-h-[70px] h-[70px]"
      : "bg-transparent",
  ].join(" ");

  const listItemBase =
    "relative group  hover:text-brand-yellow transition-all duration-200";
  const selectedClass = "underline decoration-[1.5px] underline-offset-[5px]";
  const forceLightLogoOnHomeHero =
    location.pathname === "/" && !hasBackground && heroHoveredPanel !== null;
  const logoSrc = forceLightLogoOnHomeHero
    ? logo.light
    : hasBackground
    ? logo.dark
    : isLight
    ? logo.light
    : logo.dark;
  const listTextColor =
    !hasBackground && isLight ? "text-white" : "text-text-slate";

  return (
    <header
      className={headerClass}
      onClick={(e) => {
        if ((e.target as HTMLElement).tagName === "A") {
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      }}
    >
      <div className="w-full px-5 py-4 flex items-center justify-between">
        <NavLink to="/" onClick={closeMenu}>
          <img
            src={logoSrc}
            alt="Letting Engine logo in yellow and white"
            className="h-12"
          />
        </NavLink>
        <ul
          className={`flex relative whitespace-nowrap font-manrope gap-6 text-base font-light pr-3 ${listTextColor} items-center`}
        >
          <li
            className={`${listItemBase} ${
              isSelected("/services/rent") ? selectedClass : ""
            }`}
          >
            <NavLink to="/services/rent">Rent</NavLink>
          </li>
          <li
            className={`${listItemBase} ${
              isSelected("/services/buy") ? selectedClass : ""
            }`}
          >
            <NavLink to="/services/buy">Buy</NavLink>
          </li>
          <ExpandList
            to="/services/landlords"
            header="Landlords"
            list={
              <>
                <li className="px-2 py-1">
                  <NavLink to="/services/stamp-duty-calculator?language=english">
                    Stamp Duty Calculator
                  </NavLink>
                </li>
                <li className="px-2 py-1">
                  <NavLink to="/services/rental-yield?language=english">
                    Rental Yield
                  </NavLink>
                </li>
                <li className="px-2 py-1">
                  <NavLink to="/services/rent-estimate?language=english">
                    Rent Estimator
                  </NavLink>
                </li>
                <li className="px-2 py-1">
                  <NavLink to="/services/mortgage-calculator?language=english">
                    Mortgage Calculator
                  </NavLink>
                </li>
              </>
            }
            isSelected={isSelected}
            isOpen={expandListStates["landlords"]}
            toggleExpandList={() => toggleExpandList("landlords")}
            listItemBase={listItemBase}
            selectedClass={selectedClass}
          />
          <li
            className={`${listItemBase} ${
              isSelected("/about-us") ? selectedClass : ""
            }`}
          >
            <NavLink to="/about-us#regulatory-stack">Compliance</NavLink>
          </li>
          <li
            className={`${listItemBase} ${
              isSelected("/about-us") ? selectedClass : ""
            }`}
          >
            <NavLink to="/about-us">About Us</NavLink>
          </li>
          <li
            className={`${listItemBase} ${
              isSelected("/contact-us") ? selectedClass : ""
            }`}
          >
            <NavLink to="/contact-us">Contact Us</NavLink>
          </li>

          <li
            className={`${listItemBase} ${
              isSelected("/careers") ? selectedClass : ""
            }`}
          >
            <NavLink to="/careers">Careers</NavLink>
          </li>
        </ul>
      </div>
    </header>
  );
}

interface ExpandListProps {
  header: string;
  to: string;
  list: React.ReactNode;
  isSelected: (path: string) => boolean;
  isOpen: boolean;
  toggleExpandList: () => void;
  listItemBase: string;
  selectedClass: string;
}

function ExpandList({
  header,
  to,
  list,
  isSelected,
  listItemBase,
  selectedClass,
}: ExpandListProps): JSX.Element {
  const selected = isSelected(to);

  return (
    <li className={`relative ${listItemBase} ${selected ? selectedClass : ""}`}>
      <NavLink to={to} className="relative z-[100]">
        {header}
      </NavLink>
      <div
        className={
          "hidden group-hover:flex absolute top-full left-0 pt-2 min-w-[220px] z-[90]"
        }
      >
        <ul className="px-3 flex flex-col gap-3 py-2 bg-white border border-solid border-[rgba(0,0,0,0.3)] shadow-soft">
          {list}
        </ul>
      </div>
    </li>
  );
}
